import React from 'react';
import './Legal.css';

const Privacy: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <h1>Privacy Policy</h1>
        <div className="legal-content">
          <p>
            FreioLabs ("we," "our," or "us") is committed to protecting your privacy and maintaining the confidentiality 
            of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your 
            information when you use our applications, websites, services, and digital products (collectively referred to as "Services").
          </p>

          <p>
            This Privacy Policy applies to all Services developed and operated by FreioLabs, including mobile applications, 
            web applications, software tools, APIs, and any future digital products or services we may offer.
          </p>

          <h2>1. Information We Collect</h2>
          <p>
            We may collect various types of information from and about users of our Services, including:
          </p>

          <h3>1.1 Personal Information You Provide</h3>
          <p>
            Information that you voluntarily provide to us when using our Services, such as:
          </p>
          <ul>
            <li>Name, username, and display name</li>
            <li>Email address and contact information</li>
            <li>Phone number (when required)</li>
            <li>Profile information and preferences</li>
            <li>Account credentials and authentication data</li>
            <li>User-generated content (posts, comments, uploads)</li>
            <li>Communication with our support team</li>
            <li>Survey responses and feedback</li>
            <li>Payment and billing information</li>
          </ul>

          <h3>1.2 Automatically Collected Information</h3>
          <p>
            Information that our Services automatically collect when you access or use them:
          </p>
          <ul>
            <li>Device information (model, operating system, unique identifiers)</li>
            <li>IP address and approximate geographic location</li>
            <li>Browser type and version</li>
            <li>Usage data and interaction patterns</li>
            <li>Log files and error reports</li>
            <li>Time stamps and session duration</li>
            <li>Referral sources and page views</li>
            <li>Network connection information</li>
          </ul>

          <h3>1.3 Location Information</h3>
          <p>
            Depending on the Service and your device settings, we may collect location information to:
          </p>
          <ul>
            <li>Provide location-based features and content</li>
            <li>Improve service functionality and user experience</li>
            <li>Analyze usage patterns and trends</li>
            <li>Provide relevant recommendations</li>
            <li>Ensure security and prevent fraud</li>
          </ul>

          <h3>1.4 Third-Party Authentication</h3>
          <p>
            When you choose to authenticate through third-party services, we may receive:
          </p>
          <ul>
            <li>Profile information from social media platforms</li>
            <li>Email addresses and contact details</li>
            <li>Public profile data</li>
            <li>Friend lists and social connections (with permission)</li>
          </ul>

          <h2>2. How We Use Your Information</h2>
          <p>
            We use the information we collect for various legitimate business purposes, including:
          </p>

          <h3>2.1 Service Provision</h3>
          <ul>
            <li>Creating and managing user accounts</li>
            <li>Providing access to our Services</li>
            <li>Personalizing user experience</li>
            <li>Processing transactions and payments</li>
            <li>Delivering requested services and content</li>
          </ul>

          <h3>2.2 Communication</h3>
          <ul>
            <li>Sending important service notifications</li>
            <li>Providing customer support</li>
            <li>Responding to inquiries and requests</li>
            <li>Sending promotional communications (with consent)</li>
            <li>Delivering push notifications and alerts</li>
          </ul>

          <h3>2.3 Improvement and Analytics</h3>
          <ul>
            <li>Analyzing usage patterns and trends</li>
            <li>Improving existing Services and developing new ones</li>
            <li>Conducting research and analytics</li>
            <li>Testing new features and functionality</li>
            <li>Monitoring performance and reliability</li>
          </ul>

          <h3>2.4 Security and Legal Compliance</h3>
          <ul>
            <li>Protecting against fraud and security threats</li>
            <li>Enforcing our Terms of Service</li>
            <li>Complying with legal obligations</li>
            <li>Protecting our rights and property</li>
            <li>Ensuring user safety</li>
          </ul>

          <h2>3. Information Sharing and Disclosure</h2>
          <p>
            We do not sell, rent, or trade your personal information to third parties. We may share your information only in the following circumstances:
          </p>

          <h3>3.1 Service Providers</h3>
          <p>
            We may share information with trusted third-party service providers who assist us in operating our Services, including:
          </p>
          <ul>
            <li>Cloud hosting and storage providers</li>
            <li>Analytics and performance monitoring services</li>
            <li>Payment processing services</li>
            <li>Customer support platforms</li>
            <li>Email and communication services</li>
            <li>Security and fraud prevention services</li>
          </ul>

          <h3>3.2 Legal Requirements</h3>
          <p>
            We may disclose your information when required by law or when we believe disclosure is necessary to:
          </p>
          <ul>
            <li>Comply with legal process or government requests</li>
            <li>Enforce our Terms of Service</li>
            <li>Protect our rights, property, or safety</li>
            <li>Protect the rights, property, or safety of our users</li>
            <li>Prevent or investigate suspected fraud or illegal activity</li>
          </ul>

          <h3>3.3 Business Transfers</h3>
          <p>
            In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. 
            We will provide notice before your information is transferred and becomes subject to a different privacy policy.
          </p>

          <h3>3.4 Consent</h3>
          <p>
            We may share your information with your explicit consent or at your direction.
          </p>

          <h2>4. Data Security</h2>
          <p>
            We implement appropriate technical, administrative, and physical security measures to protect your personal information against 
            unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Secure authentication and access controls</li>
            <li>Regular security audits and assessments</li>
            <li>Employee training on data protection</li>
            <li>Incident response and monitoring procedures</li>
          </ul>
          <p>
            However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect 
            your personal information, we cannot guarantee its absolute security.
          </p>

          <h2>5. Data Retention</h2>
          <p>
            We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, 
            unless a longer retention period is required or permitted by law. Factors we consider when determining retention periods include:
          </p>
          <ul>
            <li>The nature and purpose of the data collection</li>
            <li>Legal and regulatory requirements</li>
            <li>Your account status and activity</li>
            <li>Our legitimate business interests</li>
            <li>Dispute resolution and legal proceedings</li>
          </ul>

          <h2>6. Your Rights and Choices</h2>
          <p>
            Depending on your location and applicable laws, you may have the following rights regarding your personal information:
          </p>

          <h3>6.1 Access and Portability</h3>
          <ul>
            <li>Request access to your personal information</li>
            <li>Receive a copy of your data in a portable format</li>
            <li>Request information about how we process your data</li>
          </ul>

          <h3>6.2 Correction and Updates</h3>
          <ul>
            <li>Correct inaccurate or incomplete information</li>
            <li>Update your account information and preferences</li>
          </ul>

          <h3>6.3 Deletion</h3>
          <ul>
            <li>Request deletion of your account and personal information</li>
            <li>Exercise the "right to be forgotten" where applicable</li>
          </ul>

          <h3>6.4 Restriction and Objection</h3>
          <ul>
            <li>Restrict certain processing of your information</li>
            <li>Object to processing based on legitimate interests</li>
            <li>Opt-out of marketing communications</li>
          </ul>

          <h3>6.5 Consent Withdrawal</h3>
          <ul>
            <li>Withdraw consent for data processing (where consent is the legal basis)</li>
            <li>Modify app permissions through device settings</li>
          </ul>

          <h2>7. Children's Privacy</h2>
          <p>
            Our Services are not intended for children under the age of 13 (or the minimum age in your jurisdiction). 
            We do not knowingly collect personal information from children under 13. If we become aware that we have 
            collected personal information from a child under 13, we will take steps to delete that information promptly.
          </p>
          <p>
            Parents and guardians who believe their child has provided personal information to us should contact us immediately.
          </p>

          <h2>8. International Data Transfers</h2>
          <p>
            Your information may be processed and stored in countries other than your country of residence. These countries 
            may have data protection laws that differ from those in your country. When we transfer your information 
            internationally, we ensure appropriate safeguards are in place to protect your privacy.
          </p>

          <h2>9. Third-Party Services</h2>
          <p>
            Our Services may integrate with or contain links to third-party services, including:
          </p>
          <ul>
            <li>Social media platforms</li>
            <li>Analytics services (Google Analytics, Firebase)</li>
            <li>Authentication providers (Google, Facebook, Apple)</li>
            <li>Payment processors</li>
            <li>Advertising networks</li>
            <li>Other third-party APIs and services</li>
          </ul>
          <p>
            These third-party services have their own privacy policies. We encourage you to review their privacy practices 
            before providing them with your information.
          </p>

          <h2>10. California Privacy Rights</h2>
          <p>
            California residents have specific rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA), including:
          </p>
          <ul>
            <li>Right to know what personal information is collected, used, shared, or sold</li>
            <li>Right to delete personal information</li>
            <li>Right to opt-out of the sale of personal information</li>
            <li>Right to non-discrimination for exercising privacy rights</li>
            <li>Right to correct inaccurate personal information</li>
            <li>Right to limit the use and disclosure of sensitive personal information</li>
          </ul>

          <h2>11. Cookie Policy</h2>
          <p>
            We use cookies and similar tracking technologies to enhance your experience with our Services. 
            These technologies help us:
          </p>
          <ul>
            <li>Remember your preferences and settings</li>
            <li>Analyze how you use our Services</li>
            <li>Provide personalized content and features</li>
            <li>Improve security and prevent fraud</li>
          </ul>
          <p>
            You can control cookie preferences through your browser settings, but disabling cookies may affect 
            the functionality of our Services.
          </p>

          <h2>12. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices, technology, 
            legal requirements, or other factors. When we make material changes, we will:
          </p>
          <ul>
            <li>Post the updated Privacy Policy on this page</li>
            <li>Update the "Last Updated" date</li>
            <li>Notify you through our Services or by email (for significant changes)</li>
            <li>Obtain your consent where required by law</li>
          </ul>
          <p>
            Your continued use of our Services after the effective date of any changes constitutes your acceptance 
            of the revised Privacy Policy.
          </p>

          <h2>13. Contact Us</h2>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
            please contact us at:
          </p>
          <p>
            <strong>Email:</strong> legal@freiolabs.com<br/>
            <strong>Website:</strong> freiolabs.com<br/>
            <strong>Subject Line:</strong> Privacy Policy Inquiry
          </p>
          <p>
            For data deletion requests, please include "Data Deletion Request" in the subject line and provide 
            sufficient information to verify your identity.
          </p>

          <p className="effective-date">
            Last Updated: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>

          <div className="related-documents">
            <h2>Related Documents</h2>
            <ul>
              <li><a href="/terms">Terms of Service</a></li>
              <li><a href="/liability">Release of Liability</a></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;